/**
 * EXECUTION RUNTIME — the display vocabulary, and nothing else.
 *
 * Pure and dependency-free so every rule below is drivable by the test
 * harness. This module maps backend-owned enum values to words and tones. It
 * computes no state, aggregates nothing, and knows no payload shape.
 *
 * THE KEYS ARE THE BACKEND'S OWN ENUM VALUES, DELIBERATELY.
 *
 * `what-can-start-2` classifies every activity instance into exactly six
 * states. Naming the frontend's keys anything else would create a translation
 * layer, and a translation layer is where a sixth state quietly becomes a
 * fifth. So `CAN_START_NOW` is spelled `CAN_START_NOW` here.
 *
 * THE FIVE RULES THIS MODULE ENFORCES
 *
 * 1. UNKNOWN IS NOT SAFE, CLEAR, OR ZERO. It is the absence of an answer, and
 *    it renders as an absence. A screen that lets UNKNOWN read as "fine" will
 *    have someone start work over an unanswered question.
 *
 * 2. CONFLICT IS NOT FAILURE. Two sources disagreeing is a real, actionable,
 *    distinct state: the answer exists twice and OMNΛS refuses to pick. Fold
 *    it into FAIL and the resolution — read both, decide — disappears.
 *
 * 3. NO SCORE, NO RANK, NO PRIORITY. There is no readiness number here and no
 *    ordering of constraints by importance. "Resolve this first" is a
 *    conclusion, and no merged contract owns it.
 *
 * 4. IN_PROGRESS AND COMPLETE ARE NOT KINDS OF "CAN START". They answer a
 *    different question. Collapsing them into a startability bucket would put
 *    running work on a list of work to mobilize.
 *
 * 5. CURRENT, HYPOTHETICAL AND AS_RECORDED ARE THREE DIFFERENT THINGS. Live
 *    state, a scenario that was never applied, and a frozen record cannot
 *    look alike.
 *
 * Nothing here transitions, derives, or infers. These are labels for values
 * the backend already decided.
 */

/**
 * The six states of `what-can-start-2`, verbatim. UNKNOWN is no longer
 * residual: it is the answer whenever a hard gate is not established either
 * way, and it withholds the work without asserting anything against it.
 *
 * Four of them answer "can this start?". Two of them say the question no
 * longer applies. See `RUNTIME_STATE_FAMILY`.
 */
export const RUNTIME_STATES = [
  'CAN_START_NOW',
  'CAN_START_CONDITIONALLY',
  'BLOCKED',
  'UNKNOWN',
  'IN_PROGRESS',
  'COMPLETE',
];

/**
 * What KIND of answer each state is — static metadata about the words
 * themselves, not a judgement about any project.
 *
 * This exists so a caller can tell "this state answers the startability
 * question" from "this state supersedes it" WITHOUT hardcoding a list of
 * strings at six different call sites. It is not a startability computation:
 * when the backend sends `canStart`, render `canStart`.
 */
export const RUNTIME_STATE_FAMILY = {
  CAN_START_NOW: 'STARTABILITY',
  CAN_START_CONDITIONALLY: 'STARTABILITY',
  BLOCKED: 'STARTABILITY',
  UNKNOWN: 'STARTABILITY',
  IN_PROGRESS: 'PROGRESS',
  COMPLETE: 'PROGRESS',
};

const RUNTIME_STATE_DISPLAY = {
  CAN_START_NOW: {
    label: 'Can start now',
    tone: 'good',
    meaning: 'Every condition this work depends on is satisfied.',
  },
  CAN_START_CONDITIONALLY: {
    /**
     * Deliberately its own state rather than a shade of BLOCKED: "can start
     * once X" and "cannot start" send a superintendent to two different
     * places.
     */
    label: 'Can start once conditions are met',
    tone: 'attention',
    meaning: 'Nothing is blocking outright, but conditions remain outstanding.',
  },
  BLOCKED: {
    label: 'Blocked',
    tone: 'bad',
    meaning: 'At least one condition prevents this work from proceeding.',
  },
  UNKNOWN: {
    /**
     * NOT "clear", NOT "pending", NOT "ready". The label has to carry that a
     * question is unanswered, because the operational cost of reading this as
     * "fine" is someone mobilizing a crew against an unknown.
     */
    label: 'Not established',
    tone: 'unknown',
    meaning:
      'OMNΛS cannot say whether this work can proceed. That is an open question, and it is not permission to start.',
  },
  IN_PROGRESS: {
    /**
     * Its own tone, never `good`. Running work on a "what can I start" list
     * is how a crew gets sent to a workface that already has one.
     */
    label: 'In progress',
    tone: 'active',
    meaning: 'Crews are on this work. Whether it could start is no longer the open question.',
  },
  COMPLETE: {
    label: 'Complete',
    tone: 'done',
    meaning: 'This work is recorded as finished. Whether it can start no longer applies.',
  },
};

export const runtimeStateDisplay = (state) =>
  RUNTIME_STATE_DISPLAY[state] || RUNTIME_STATE_DISPLAY.UNKNOWN;

/**
 * Whether a state is an answer to "can this start?".
 *
 * A convenience over `RUNTIME_STATE_FAMILY`, not a startability verdict: it
 * classifies the WORD, and knows nothing about any instance.
 */
export const isStartabilityQuestion = (state) =>
  RUNTIME_STATE_FAMILY[state] === 'STARTABILITY';

/**
 * The truth state of one fact the runtime evaluated.
 *
 * CONFLICT sits beside FAIL rather than inside it. A failed condition has one
 * answer and the answer is no; a conflicted one has two answers and OMNΛS
 * declines to choose between them. Collapsing them would hide the only state
 * whose resolution is "a human reads both sources".
 */
export const TRUTH_STATES = ['PASS', 'FAIL', 'UNKNOWN', 'CONFLICT'];

const TRUTH_STATE_DISPLAY = {
  PASS: {
    label: 'Satisfied',
    tone: 'good',
    meaning: 'A source states this is met.',
  },
  FAIL: {
    label: 'Not satisfied',
    tone: 'bad',
    meaning: 'A source states this is not met.',
  },
  UNKNOWN: {
    label: 'Unknown',
    tone: 'unknown',
    meaning: 'No source states this either way. Nothing has been assumed.',
  },
  CONFLICT: {
    label: 'Sources disagree',
    tone: 'attention',
    meaning:
      'More than one source answers this, and they do not agree. OMNΛS keeps both and picks neither — resolving it is a human decision.',
  },
};

export const truthStateDisplay = (state) =>
  TRUTH_STATE_DISPLAY[state] || TRUTH_STATE_DISPLAY.UNKNOWN;

/**
 * Words this vocabulary must never use, checked by test.
 *
 * Each is a way of quietly converting an absence into a reassurance, a
 * disagreement into a defeat, or a set of facts into a ranking. They are
 * listed rather than merely avoided so a future edit that reaches for one
 * fails a test instead of shipping.
 */
export const FORBIDDEN_RUNTIME_WORDS = {
  /** UNKNOWN must never read as an all-clear. */
  unknownAsSafe: ['safe', 'clear', 'ready', 'fine', 'ok', 'none', 'no issues', 'all good'],
  /** CONFLICT must never read as a failure. */
  conflictAsFailure: ['failed', 'failure', 'invalid', 'error', 'rejected', 'broken'],
  /** No surface here ranks, scores or sequences constraints. */
  rankingOrScore: [
    'score',
    'priority',
    'criticality',
    'leverage score',
    'ai priority',
    'resolve this first',
    'most important',
    'rank',
    'severity score',
    'readiness score',
  ],
  /** Exposure is an upper bound, never a verdict about who caused what. */
  causalBlame: [
    'caused',
    'is delaying',
    'will delay',
    'responsible for',
    'at fault',
    'to blame',
    'late',
  ],
};

/**
 * WHEN a panel's facts were true — three modes, not two.
 *
 * `CURRENT`      live canonical state.
 * `HYPOTHETICAL` a counterfactual that was computed and never applied.
 *                `counterfactual-execution-1` labels every one of its
 *                responses this way and writes nothing.
 * `AS_RECORDED`  a frozen record of what was known at a past moment.
 *                `decision-episode-1` never refreshes it.
 *
 * A scenario that looks like live state, or a historical envelope that looks
 * like live state, are the two most misleading things this product could
 * render — so each carries a WORD, not just a colour.
 */
export const TEMPORALITY = ['CURRENT', 'HYPOTHETICAL', 'AS_RECORDED'];

const TEMPORALITY_DISPLAY = {
  CURRENT: {
    label: 'Current state',
    mark: 'CURRENT',
    tone: 'current',
    meaning: 'What OMNΛS reads right now. It changes as the project changes.',
  },
  HYPOTHETICAL: {
    label: 'Hypothetical',
    mark: 'HYPOTHETICAL',
    tone: 'hypothetical',
    meaning:
      'A scenario OMNΛS worked through without changing anything. Nothing here has been applied to the project.',
  },
  AS_RECORDED: {
    label: 'As recorded',
    mark: 'AS RECORDED',
    tone: 'historical',
    meaning:
      'What was known at the time this was recorded. It does not change when the project changes.',
  },
};

/**
 * An unrecognised temporality falls back to CURRENT deliberately.
 *
 * Under-labelling live state is a missing badge. Mislabelling live state as a
 * scenario or as history invites someone to ignore a real blocker.
 */
export const temporalityDisplay = (temporality) =>
  TEMPORALITY_DISPLAY[temporality] || TEMPORALITY_DISPLAY.CURRENT;

/**
 * A source reference, presented without pretending to be more than it is.
 *
 * The runtime identifies a constraint by the ROW that determines it
 * (`sourceTable` + `sourceIds`). Those are database identities: useful for
 * grouping and for diagnostics, and meaningless to a superintendent. So the
 * primary rendering is the human label when one exists, and the raw identity
 * is available beneath rather than as the headline.
 *
 * No id is invented. When the backend names no source, this says the source
 * is unnamed rather than showing a blank.
 */
export const presentSourceReference = (reference) => {
  // `= {}` would cover undefined and NOT null, and a null here is exactly
  // what a backend field that could not be established looks like.
  const { sourceTable, sourceIds, label } = reference || {};
  const ids = Array.isArray(sourceIds) ? sourceIds.filter(Boolean) : [];
  const table = sourceTable || null;
  return {
    /** What a human reads. */
    label: label || SOURCE_TABLE_LABEL[table] || null,
    /** Whether there is anything to show at all. */
    named: Boolean(label || table),
    table,
    ids,
    count: ids.length,
    /** Diagnostics, never the headline. */
    detail: table && ids.length ? `${table}: ${ids.join(', ')}` : table || null,
  };
};

/**
 * The canonical tables the runtime names, in construction language.
 *
 * Only tables a merged backend actually populates are listed. An unmapped
 * table falls through to null rather than to a guessed noun — a wrong label
 * on a source is worse than no label, because it sends someone to the wrong
 * record.
 *
 * `constraint-leverage-1` also keys INSTANCE_SCOPED constraints as
 * `instance:{instanceId}:{gateKey}`. That is not a table and gets no label;
 * the contract supplies a `title` from the owning row instead.
 */
export const SOURCE_TABLE_LABEL = {
  material_status: 'Material',
  activity_instances: 'Predecessor activity',
  locations: 'Location access',
  project_requirements: 'Requirement',
};

export const sourceTableLabel = (table) => SOURCE_TABLE_LABEL[table] || null;

/**
 * Contract-version guard.
 *
 * Every runtime payload names the contract it was built to. A screen written
 * against `provider-outcome-2` that receives `provider-outcome-3` must not
 * guess: an additive version would render fine by luck, and a breaking one
 * would render confidently wrong. Both are worse than saying so.
 *
 * Returns `matches` plus the sentence to show. `received` being absent is its
 * own case — an unnamed version is not a matching one, but it is also not
 * evidence of a mismatch, so it is reported as unnamed rather than as wrong.
 */
export const presentContractVersion = (received, supported) => {
  const named = typeof received === 'string' && received.length > 0;
  const matches = named && received === supported;
  return {
    matches,
    named,
    received: named ? received : null,
    supported,
    message: matches
      ? null
      : named
        ? `This view supports ${supported}. The server returned ${received}.`
        : `This view supports ${supported}. The server did not name a contract version.`,
  };
};

/* -------------------------------------------------------------------------
 * PLATFORM GAP — why OMNΛS could not establish a gate (`what-can-start-2`)
 * ---------------------------------------------------------------------- */

/**
 * The closed vocabulary, verbatim from `sa_gating_engine.PLATFORM_GAPS`.
 *
 * A gate carrying one of these is ALWAYS `UNKNOWN` — never PASSED, never
 * NOT_REQUIRED. It explains an unknown and never decides one: a property test
 * on the backend walks every service module's AST to prove no branch reads it
 * to make a decision. Nothing here may read it to make one either.
 *
 * The distinction this exists to protect: a platform gap is OMNΛS missing a
 * model, an owner, a writer or a vocabulary. **It is never a construction
 * failure and never anyone on site failing to do something.**
 */
export const PLATFORM_GAPS = [
  'MISSING_EXECUTION_MODEL_LINKAGE',
  'PLACEHOLDER_SCOPE',
  'NO_PRODUCTION_WRITER',
  'UNSUPPORTED_SOURCE_VOCABULARY',
  'UNRESOLVED_SOURCE_REFERENCE',
  'MISSING_CANONICAL_OWNER',
  'NO_MATERIAL_RECORDS',
];

/**
 * `needs` names the KIND of thing that has to change, drawn from the
 * contract's own definition of the token. It is deliberately not a person,
 * a role, or an assignment: the payload names no responsible party, and
 * inventing one here would manufacture authority the backend never stated.
 */
const PLATFORM_GAP_DISPLAY = {
  MISSING_EXECUTION_MODEL_LINKAGE: {
    key: 'MISSING_EXECUTION_MODEL_LINKAGE',
    label: 'Execution model linkage is not recorded',
    meaning:
      'This work has no variant and no cost code, and nothing in OMNΛS writes those columns today. The work is unlinked, not unscoped — nobody failed to define it.',
    needs: 'a record in the project model',
  },
  PLACEHOLDER_SCOPE: {
    key: 'PLACEHOLDER_SCOPE',
    label: 'The quantity is a placeholder, not a measurement',
    meaning:
      'Activation wrote a literal 1.0 “LS” as structure rather than measurement. No measured quantity has been recorded for this work.',
    needs: 'a measured quantity',
  },
  NO_PRODUCTION_WRITER: {
    key: 'NO_PRODUCTION_WRITER',
    label: 'Nothing in OMNΛS writes this column',
    meaning:
      'The column has a default and no writer anywhere, so its value carries no information about the world. The default is not evidence.',
    needs: 'a writer in the platform',
  },
  UNSUPPORTED_SOURCE_VOCABULARY: {
    key: 'UNSUPPORTED_SOURCE_VOCABULARY',
    label: 'The source used a value OMNΛS cannot read',
    meaning:
      'A vocabulary OMNΛS does not understand is not a real-world negative. The value is preserved and not interpreted.',
    needs: 'a vocabulary the integration and OMNΛS share',
  },
  UNRESOLVED_SOURCE_REFERENCE: {
    key: 'UNRESOLVED_SOURCE_REFERENCE',
    label: 'A referenced record could not be resolved',
    meaning:
      'A referenced row was not resolvable in this scope. Its id is preserved verbatim rather than dropped through the join.',
    needs: 'the referenced record to exist in this scope',
  },
  MISSING_CANONICAL_OWNER: {
    key: 'MISSING_CANONICAL_OWNER',
    label: 'The record that says whether this applies does not exist',
    meaning:
      'Applicability itself is unestablished — in either direction. OMNΛS cannot say this gate applies, and cannot say it does not.',
    needs: 'a record stating whether this applies',
  },
  NO_MATERIAL_RECORDS: {
    key: 'NO_MATERIAL_RECORDS',
    label: 'No material records exist for this work',
    meaning:
      'Row absence is not a statement that the work needs no materials. No column anywhere asserts that, so OMNΛS does not assume it.',
    needs: 'material records, or a record stating none are required',
  },
};

/**
 * The payload names no responsible party for a platform gap, so neither does
 * this. Rendered beside every gap so the reader is not left to guess.
 */
export const PLATFORM_GAP_OWNER_UNKNOWN =
  'Who resolves this on this project: not established. OMNΛS records no responsible party for a platform gap.';

export const PLATFORM_GAP_NOT_A_FAILURE =
  'This is OMNΛS missing something, not work failing on site. No established field failure is being asserted.';

export const platformGapDisplay = (gap) =>
  PLATFORM_GAP_DISPLAY[gap] || {
    key: String(gap ?? 'UNKNOWN'),
    label: 'A platform gap this view does not name',
    meaning:
      'The server named a platform gap this view does not carry. It is shown verbatim rather than mapped onto one that looks close.',
    needs: null,
  };
